import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  FileText, 
  Target, 
  BarChart3, 
  Users,
  Search,
  PenTool,
  CheckCircle,
  BookOpen,
  Lightbulb,
  Settings,
  Award,
  Quote
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const whyChooseUs = [
  {
    icon: Target,
    title: 'Strategic Alignment',
    description: 'We ensure your project addresses the specific "pain points" and KPIs of global and local health donors.'
  },
  {
    icon: Award,
    title: 'Technical Authority',
    description: 'Our writers are experts in public health, health systems strengthening, and clinical innovation.'
  },
  {
    icon: BarChart3,
    title: 'Data-Driven Narratives',
    description: 'We translate complex health data into clear, persuasive arguments for investment.'
  },
  {
    icon: Settings,
    title: 'End-to-End Support',
    description: 'From identifying the right opportunities to the final submission, we handle the heavy lifting.'
  }
];

const services = [
  {
    icon: Search,
    title: 'Prospect Research',
    description: 'Identification of funding opportunities (foundations, government, and international agencies) that match your mission.'
  },
  {
    icon: FileText,
    title: 'Full Proposal Development',
    description: 'Drafting of the narrative, theory of change, logical frameworks, and budget justifications.'
  },
  {
    icon: PenTool,
    title: 'Grant Editing & Review',
    description: 'A rigorous "red-team" review of your existing draft to improve flow, compliance, and persuasiveness.'
  },
  {
    icon: BookOpen,
    title: 'Capacity Building',
    description: 'Training your internal team on the art and science of sustainable fundraising.'
  }
];

const processSteps = [
  {
    number: '01',
    title: 'Discovery & Strategy',
    description: 'We dive deep into your project goals and the funder\'s requirements.',
    icon: Lightbulb
  },
  {
    number: '02',
    title: 'Framework Design',
    description: 'We develop the Theory of Change (ToC) and logic model that form the backbone of the proposal.',
    icon: Target
  },
  {
    number: '03',
    title: 'Collaborative Drafting',
    description: 'Our experts craft the narrative while maintaining a constant feedback loop with your team.',
    icon: Users
  },
  {
    number: '04',
    title: 'Quality Assurance',
    description: 'A final polish for compliance, tone, and technical accuracy to ensure no points are left on the table.',
    icon: CheckCircle
  }
];

export const GrantWritingPage = () => {
  return (
    <div data-testid="grant-writing-page">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-gradient-to-b from-slate-50 to-white" data-testid="grant-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Expert Grant Writing Services
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Turn Vision into Impact with Expert Grant Writing
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8"
            >
              <span className="text-teal-600 font-semibold">Bridging the gap</span> between groundbreaking 
              health strategies and the funding they deserve.
            </motion.p>

            <motion.p 
              variants={itemVariants}
              className="text-base md:text-lg text-slate-500 leading-relaxed"
            >
              At HSL, we understand that a winning grant proposal is more than just a document—it's a 
              strategic roadmap. In an increasingly competitive funding landscape, health organizations 
              need more than technical accuracy; they need a compelling narrative that aligns with donor 
              priorities and demonstrates sustainable impact.
            </motion.p>

            <motion.div 
              variants={itemVariants}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center justify-center gap-2 group"
                data-testid="grant-hero-cta"
              >
                Contact Us for a Consultation
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose HSL Section */}
      <section className="section-padding" data-testid="why-choose-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Our Advantage
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Why Choose HSL for Your Grant Development?
            </h2>
            <p className="text-slate-600 text-lg">
              We don't just fill out forms. We leverage our core expertise in <span className="text-teal-600 font-semibold">Health Strategy 
              and Leadership</span> to position your organization as the clear choice for funders.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-8 hover:border-teal-500 hover:shadow-lg transition-all group"
                data-testid={`why-choose-${index}`}
              >
                <div className="w-14 h-14 bg-teal-100 border border-teal-200 flex items-center justify-center mb-6 group-hover:bg-teal-500 transition-colors">
                  <item.icon size={28} className="text-teal-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-slate-50" data-testid="grant-services-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              What We Offer
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              Our Grant Writing Services
            </h2>
            <p className="text-slate-600 text-lg">
              We offer a comprehensive suite of services tailored to NGOs, healthcare providers, and research institutions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-8 hover:border-slate-300 transition-colors"
                data-testid={`grant-service-${index}`}
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <service.icon size={24} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-slate-500 leading-relaxed">{service.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding" data-testid="process-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Our Methodology
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
              The HSL Process: Our Formula for Success
            </h2>
            <p className="text-slate-600 text-lg">
              We follow a disciplined approach to ensure every submission is of the highest caliber.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <div className="bg-white border border-slate-200 p-8 h-full hover:border-teal-500 transition-colors group">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-heading text-4xl font-bold text-teal-600/20 group-hover:text-teal-600/40 transition-colors">
                      {step.number}
                    </span>
                    <div className="w-10 h-10 bg-teal-100 flex items-center justify-center">
                      <step.icon size={20} className="text-teal-600" />
                    </div>
                  </div>
                  <h3 className="font-heading text-lg text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
                </div>
                
                {/* Connector Arrow (hidden on last item and mobile) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight size={20} className="text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding bg-teal-600" data-testid="quote-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Quote size={48} className="text-white/30 mx-auto mb-6" />
            <blockquote className="font-heading text-2xl md:text-3xl text-white leading-relaxed mb-6">
              "Funding is the fuel for health equity. We provide the engine."
            </blockquote>
            <p className="text-teal-100 text-lg">HSL — Leading the way in Health Strategy.</p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-50" data-testid="grant-cta-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden bg-white border border-slate-200 p-12 md:p-16 text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-teal-500/5" />
            
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Ready to Secure Your Next Award?
              </h2>
              <p className="text-slate-500 text-lg mb-8 max-w-2xl mx-auto">
                Don't let a lack of time or specialized writing expertise stand in the way of your 
                organization's growth. Let HSL's team of strategists and writers secure the resources 
                you need to lead.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center justify-center gap-2 group"
                  data-testid="grant-cta-primary"
                >
                  Contact Us Today for a Consultation
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="grant-cta-secondary"
                >
                  View All Services
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
